import { useLocation } from 'react-router-dom';
import { 
  IS_REPORTS_PAGE,
  IS_WORKBOARD,
} from 'dashboard409a/common/constants';
import { useFindSlug } from 'dashboard409a/utilities';

/**
 * Enum representing the different slug states/pages in the dashboard
 * This centralizes the logic and makes downstream components cleaner
 */
export enum SlugState {
  // Main dashboard states
  DASHBOARD_OVERVIEW = 'DASHBOARD_OVERVIEW',
  DASHBOARD_COMPANIES = 'DASHBOARD_COMPANIES',
  DASHBOARD_PREVIOUS_REPORTS = 'DASHBOARD_PREVIOUS_REPORTS',
  DASHBOARD_OPERATIONS = 'DASHBOARD_OPERATIONS',
  DASHBOARD_SETTINGS = 'DASHBOARD_SETTINGS',
  DASHBOARD_SUBSCRIPTION = 'DASHBOARD_SUBSCRIPTION',
  DASHBOARD_INFORMATION = 'DASHBOARD_INFORMATION',
  
  // Workboard states
  WORKBOARD = 'WORKBOARD',
  ANALYST_CURRENT_JOBS = 'ANALYST_CURRENT_JOBS',
  ANALYST_PAST_JOBS = 'ANALYST_PAST_JOBS',
  ANALYST_OPPORTUNITIES = 'ANALYST_OPPORTUNITIES',
  ANALYST_MODELS = 'ANALYST_MODELS',
  // Reports/Companies states
  REPORTS_PAGE = 'REPORTS_PAGE',
  
  // Other states
  OTHER = 'OTHER',
}

// Constants for grouping related slug states
export const WORKBOARD_SLUGS = [
  SlugState.WORKBOARD,
  SlugState.ANALYST_CURRENT_JOBS,
  SlugState.ANALYST_PAST_JOBS,
  SlugState.ANALYST_OPPORTUNITIES,
  SlugState.ANALYST_MODELS,
];

export const COMPANY_DETAIL_SLUGS = [
  SlugState.DASHBOARD_SUBSCRIPTION,
  SlugState.DASHBOARD_SETTINGS,
  SlugState.DASHBOARD_INFORMATION,
];

export const MAIN_DASHBOARD_SLUGS = [
  SlugState.DASHBOARD_OVERVIEW,
  SlugState.DASHBOARD_COMPANIES,
  SlugState.DASHBOARD_PREVIOUS_REPORTS,
  SlugState.DASHBOARD_OPERATIONS,
];

/**
 * Custom hook for detecting various slug types and page states
 * Centralizes slug detection logic using an enum-based approach
 */
export const useSlugDetector = () => {
  const currentSlug = useFindSlug();
  const location = useLocation();

  // Determine the current slug state based on the slug and pathname
  const getSlugState = (): SlugState => {
    // Check for Workboard states first
    if (IS_WORKBOARD.includes(currentSlug)) {
      if (currentSlug === 'current-jobs') {
        return SlugState.ANALYST_CURRENT_JOBS;
      }
      if (currentSlug === 'past-jobs') {
        return SlugState.ANALYST_PAST_JOBS;
      }
      if (currentSlug === 'opportunities') {
        return SlugState.ANALYST_OPPORTUNITIES;
      }
      return SlugState.WORKBOARD;
    }

    // Check for reports/companies page
    if (IS_REPORTS_PAGE.some(pattern => {
      if (pattern.startsWith('/')) {
        return location.pathname.includes(pattern);
      }
      return currentSlug === pattern;
    })) {
      return SlugState.REPORTS_PAGE;
    }

    // Check for specific dashboard company states
    switch (currentSlug) {
      case 'overview':
        return SlugState.DASHBOARD_OVERVIEW;
      case 'previous-reports':
        return SlugState.DASHBOARD_PREVIOUS_REPORTS;
      case 'operations':
        return SlugState.DASHBOARD_OPERATIONS;
      case 'settings':
        return SlugState.DASHBOARD_SETTINGS;
      case 'subscription':
        return SlugState.DASHBOARD_SUBSCRIPTION;
      case 'information':
        return SlugState.DASHBOARD_INFORMATION;
      case 'companies':
        return SlugState.DASHBOARD_COMPANIES;
      case 'models':
        return SlugState.ANALYST_MODELS;
      default:
        break;
    }

    return SlugState.OTHER;
  };

  const slugState = getSlugState();

  return {
    currentSlug,
    location,
    slugState,
  };
};
